import { LinearProgress, Stack, Typography, Chip } from "@mui/material";
import {
  Warning,
  CheckCircle,
  Sync,
  Cancel,
  HourglassTop,
} from "@mui/icons-material";
import { theme } from "@theme";
import { toLocaleDateTimeString } from "@utils";
import { StructureStatus, OrderStatus, Role } from "@enums";

import { Timer } from "@volatile/pages/OrderTracker/components/PlanningAutomation/Timer";
import dayjs from "dayjs";

import {
  AutomationStatus,
  getStatusColour,
} from "@pages/OrderTracker/components/PlanningAutomation/utils";
import { OrderSearchOut } from "@providers/hop-ord-server/api";
import { useCheckUserRole } from "@utils";

export const planDeliveryColor = (planDelivery: string | null) => {
  if (planDelivery === "emergency") return "#ee3148"; // Red color for Emergency
  if (planDelivery === "urgent") return "#f7a941"; // Orange color for Urgent
  return "#848fa2"; // Grey color for Standard
};

export const getBackgroundColor = (
  orderStatus: string | null,
  planStatus: string | null | undefined,
) => {
  if (orderStatus === "manual_plan") {
    return theme.palette.success.light;
  }

  switch (planStatus) {
    case "success":
      return theme.palette.success.light;
    case "warning":
      return theme.palette.warning.light;
    case "error":
      return theme.palette.error.light;
    case "in_progress":
      return theme.palette.customBackground.main;
    default:
      return theme.palette.grey[100];
  }
};

export const structStatusColor = (status: string | null, isDraft: boolean) => {
  if (isDraft) {
    return theme.palette.grey[100];
  }

  switch (status) {
    case StructureStatus.PENDING:
      return theme.palette.warning.light;
    case StructureStatus.COMPLETE:
    case StructureStatus.SKIPPED:
      return theme.palette.success.light;
    default:
      return null;
  }
};

export const structStatusIcon = (status: string | null) => {
  switch (status) {
    case StructureStatus.COMPLETE:
      return <CheckCircle color="success" fontSize="large" />;
    case StructureStatus.SKIPPED:
      return <></>;

    default:
      return <Warning color="warning" fontSize="large" />;
  }
};

export const searchTextToolTips = [
  "Check if you have any filters applied.",
  "Verify your spelling and try again.",
  "Just received a new careplan? Please wait one minute and refresh the page. ",
  "Try using the patient's first name and last name or patient ID.",
];

export const noSearchTextToolTips = [
  "Adjusting or clearing some filters.",
  "Checking if there are any active search terms.",
];

export const StatusIcon = ({
  status,
  fontSize = "inherit",
}: {
  status: AutomationStatus;
  fontSize?: string;
}): JSX.Element => {
  if (status === "success") {
    return <CheckCircle color="success" sx={{ fontSize }} />;
  }

  if (status === "not_started") {
    return <HourglassTop color="disabled" sx={{ fontSize }} />;
  }

  if (status === "in_progress") {
    return <Sync color="secondary" sx={{ fontSize }} />;
  }

  if (status === "error") {
    return <Cancel color="error" sx={{ fontSize }} />;
  }

  return <></>;
};

export const AutomationInProgress = (props: { order: OrderSearchOut }) => {
  const planGenerationAttempt = props.order.latestPlanGenerationAttempt;
  const status = planGenerationAttempt?.status;
  const completedTime = planGenerationAttempt?.result?.finishedAt;
  const planProgressPercentage = planGenerationAttempt?.planProgressPercentage;

  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        justifyContent="flex-start"
      >
        <Chip
          label={status === "success" ? "Plan Complete" : "In Progress"}
          sx={{
            backgroundColor:
              status === "success" ? theme.palette.success.main : "#aca4fb",
            color: "#081e43",
            fontWeight: 500,
            flexShrink: 0,
            width: "168px",
            height: "28px",
            fontSize: "0.75rem",
            "& .MuiChip-label": {
              paddingLeft: "8px",
              paddingRight: "8px",
            },
            "&.MuiChip-root": {
              "&:hover": {
                backgroundColor:
                  status === "success" ? theme.palette.success.main : "#aca4fb",
                cursor: "default",
              },
              "&:hover .MuiChip-label": {
                color: "#081e43",
              },
            },
          }}
        />
      </Stack>

      <div style={{ width: "42%" }}>
        <LinearProgress
          sx={{ marginBottom: 1, marginTop: 1 }}
          variant="determinate"
          value={planProgressPercentage}
          color={getStatusColour(status as AutomationStatus)}
        />
      </div>
      <Stack direction="row">
        <Typography variant="body2" color="text.secondary"></Typography>
        {status === "not_started" && (
          <Typography variant="body2" color="disabled">
            Queued
          </Typography>
        )}
        {status === "error" && (
          <Typography variant="body2" color="error">
            Failed {toLocaleDateTimeString(completedTime || "")}
          </Typography>
        )}
        {status === "in_progress" && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "12px" }}
          >
            Processing: {(planGenerationAttempt?.currentStepIndex || 0) + 1}/
            {planGenerationAttempt?.stepSequence?.length}{" "}
            {planGenerationAttempt?.currentStep}
          </Typography>
        )}
        {status === "success" && (
          <>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "12px" }}
            >
              Elapsed time:
            </Typography>{" "}
            &nbsp;
            <Timer
              startTime={
                dayjs(planGenerationAttempt?.planningStartedAt) || null
              }
              completedTime={completedTime ? dayjs(completedTime) : null}
              currentStep={planGenerationAttempt?.currentStep}
              stepSequence={planGenerationAttempt?.stepSequence}
              orderId={props.order.id}
            />
          </>
        )}
      </Stack>
    </Stack>
  );
};

const getStructureStatusText = (
  ctStatus: string | null,
  rtstructStatus: string | null,
  isDraft: boolean,
) => {
  if (isDraft) {
    return {
      text: "Pending: CT and RTStructure does not exist",
      hasGreenTick: false,
    };
  }

  const ctComplete = ctStatus === StructureStatus.COMPLETE;
  const rtComplete = rtstructStatus === StructureStatus.COMPLETE;

  if (ctComplete && rtComplete) {
    return { text: "✓ CT and RTStructure exists", hasGreenTick: true };
  } else if (!ctComplete && !rtComplete) {
    return {
      text: "Pending: CT and RTStructure does not exist",
      hasGreenTick: false,
    };
  } else if (ctComplete && !rtComplete) {
    return { text: "Pending: RTStructure does not exist", hasGreenTick: false };
  } else if (!ctComplete && rtComplete) {
    return { text: "Pending: CT does not exist", hasGreenTick: false };
  }

  return {
    text: "Pending: CT and RTStructure does not exist",
    hasGreenTick: false,
  };
};

export const PlanningStatusCell = (props: { order: OrderSearchOut }) => {
  const isDosimetrist = useCheckUserRole(Role.DOSIMETRIST);
  const order = props.order;
  const status = order.status;
  const isDraft = status === OrderStatus.DRAFT;
  const structureStatus = getStructureStatusText(
    order.ctStatus,
    order.rtstructStatus,
    isDraft,
  );

  // Check if CT and RTStructure exist
  const ctExists = order.ctStatus === StructureStatus.COMPLETE;
  const rtStructExists = order.rtstructStatus === StructureStatus.COMPLETE;
  const structuresExist = ctExists && rtStructExists;

  // Determine button text and style based on status and structures
  const getButtonConfig = () => {
    if (status === OrderStatus.SENT_TO_AUTOMATION) {
      return {
        label: "In Progress",
        color: "#1976d2",
        backgroundColor: "#e3f2fd",
      }; // Blue
    }
    if (status === OrderStatus.READY_FOR_RO_APPROVAL) {
      return {
        label: "Pending plan review",
        color: "#081e43",
        backgroundColor: theme.palette.warning.main,
      }; // Warning (same as Physician Review "Pending" pill)
    }
    if (status === OrderStatus.APPROVED) {
      return {
        label: "Approved",
        color: "#081e43",
        backgroundColor: theme.palette.success.main,
      }; // Green
    }
    if (status === OrderStatus.REJECTED) {
      return {
        label: "Rejected",
        color: "#081e43",
        backgroundColor: theme.palette.error.main,
      }; // Green
    }
    if (status === OrderStatus.COMPLETED) {
      return {
        label: "Complete",
        color: "#081e43",
        backgroundColor: theme.palette.success.main,
      }; // Green
    }
    if (status === OrderStatus.DRAFT) {
      return { label: "Draft", color: "#f57c00", backgroundColor: "#fff3e0" }; // Orange
    }
    if (structuresExist) {
      return {
        label: "Ready for planning",
        color: "#081e43",
        backgroundColor: "#e3f2fd",
      };
    }
    return {
      label: "Not ready for planning",
      color: "#2d2d2d",
      backgroundColor: "#e8e8e8",
    };
  };

  const buttonConfig = getButtonConfig();
  const isDisabled =
    !structuresExist &&
    status !== OrderStatus.DRAFT &&
    status !== OrderStatus.SENT_TO_AUTOMATION;

  if (
    status === OrderStatus.SENT_TO_AUTOMATION ||
    (status === OrderStatus.READY_FOR_RO_APPROVAL &&
      isDosimetrist &&
      order.latestPlanGenerationAttempt?.status === "success")
  ) {
    const planStatus = order.latestPlanGenerationAttempt?.status;
    const showStructureStatus =
      planStatus !== "success" && planStatus !== "in_progress";

    return (
      <Stack gap={1}>
        <AutomationInProgress order={order} />
        {showStructureStatus && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "12px" }}
          >
            {structureStatus.hasGreenTick ? (
              <>
                <span style={{ color: "#4caf50" }}>✓</span>
                {structureStatus.text.substring(1)}
              </>
            ) : (
              structureStatus.text
            )}
          </Typography>
        )}
      </Stack>
    );
  }

  if (
    status === OrderStatus.COMPLETED ||
    (status === OrderStatus.READY_FOR_RO_APPROVAL && isDosimetrist)
  ) {
    return (
      <Stack gap={1}>
        <Chip
          label={buttonConfig.label}
          disabled={isDisabled}
          sx={{
            backgroundColor: buttonConfig.backgroundColor,
            color: buttonConfig.color,
            fontWeight: 500,
            width: "168px",
            height: "28px",
            fontSize: "0.75rem",
            cursor: "default",
            "&:hover": {
              backgroundColor: buttonConfig.backgroundColor,
            },
            "& .MuiChip-label": {
              paddingLeft: "8px",
              paddingRight: "8px",
              color: buttonConfig.color,
            },
            "&.Mui-disabled": {
              backgroundColor: buttonConfig.backgroundColor,
              opacity: 1,
              "& .MuiChip-label": {
                color: buttonConfig.color,
              },
            },
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "12px" }}
        >
          {structureStatus.hasGreenTick ? (
            <>
              <span style={{ color: "#4caf50" }}>✓</span>
              {structureStatus.text.substring(1)}
            </>
          ) : (
            structureStatus.text
          )}
        </Typography>
      </Stack>
    );
  }

  // If draft navigate to the create careplan page
  if (status === OrderStatus.DRAFT) {
    return (
      <Stack gap={1}>
        <Chip
          label={buttonConfig.label}
          sx={{
            backgroundColor: buttonConfig.backgroundColor,
            color: buttonConfig.color,
            fontWeight: 500,
            width: "168px",
            height: "28px",
            fontSize: "0.75rem",
            cursor: "default",
            "&:hover": {
              backgroundColor: buttonConfig.backgroundColor,
            },
            "& .MuiChip-label": {
              paddingLeft: "8px",
              paddingRight: "8px",
            },
            ...(status === OrderStatus.DRAFT && {
              "&.MuiChip-root": {
                "&:hover": {
                  backgroundColor: buttonConfig.backgroundColor,
                  cursor: "default",
                },
                "&:hover .MuiChip-label": {
                  color: buttonConfig.color,
                },
              },
            }),
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "12px" }}
        >
          {structureStatus.hasGreenTick ? (
            <>
              <span style={{ color: "#4caf50" }}>✓</span>
              {structureStatus.text.substring(1)}
            </>
          ) : (
            structureStatus.text
          )}
        </Typography>
      </Stack>
    );
  }

  // Otherwise use nav tracker logic and show planning button
  return (
    <Stack gap={1}>
      <Chip
        label={buttonConfig.label}
        disabled={isDisabled}
        sx={{
          backgroundColor: buttonConfig.backgroundColor,
          color: buttonConfig.color,
          fontWeight: 500,
          width: "168px",
          height: "28px",
          fontSize: "0.75rem",
          cursor: "default",
          "&:hover": {
            backgroundColor: buttonConfig.backgroundColor,
          },
          "& .MuiChip-label": {
            paddingLeft: "8px",
            paddingRight: "8px",
          },
        }}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: "12px" }}
      >
        {structureStatus.hasGreenTick ? (
          <>
            <span style={{ color: "#4caf50" }}>✓</span>
            {structureStatus.text.substring(1)}
          </>
        ) : (
          structureStatus.text
        )}
      </Typography>
    </Stack>
  );
};
